import { useQueryClient } from "@tanstack/react-query";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import {
  BulkAction,
  BulkActionHandlersByType,
  isBulkActionFinished,
  isBulkActionRunning,
} from "./types";

const DEFAULT_STORAGE_KEY = "bulk-actions-tracked-ids";

// Poll steadily while a tracked action is running, then stop. Bulk actions are
// short-lived (seconds to a few minutes) and the endpoint is cheap (~10ms), so a
// fixed interval keeps the progress bar advancing one step at a time without the
// backoff racing ahead and missing the whole run.
const POLLING_INTERVAL_MS = 2_000;

/**
 * Keep-alive poll for a finished action whose artifact is a **presigned** link.
 *
 * Without it the tray is a link that rots. A download's `downloadUrl` first
 * appears on the response that reports it complete, and the server mints it
 * fresh on every poll — so if polling stops there, that first link is also the
 * last, and it expires with the presign (`s3.downloadUrlExpiration`, one hour by
 * default). This tray is deliberately Google-Drive-shaped: it survives reloads
 * and only goes away when dismissed, so sitting open for over an hour is
 * ordinary use, and the user would then click `Descargar ZIP` and get an S3
 * `AccessDenied` blob.
 *
 * Five minutes is far enough inside the shortest presign window we ship to be
 * safe, and cheap: one request per interval per open tray, only while a
 * finished action still offers a link, and never after it is dismissed. If the
 * presign window is ever shortened below this, shorten this with it.
 */
const LINK_REFRESH_INTERVAL_MS = 5 * 60_000;

const NO_TRACKED_IDS: string[] = [];
const NO_HANDLERS: BulkActionHandlersByType = {};

/**
 * Stored ids are only ever a JSON array of strings. Anything else — unparseable
 * content, an older shape, a hand-edited value — reads back as "nothing
 * tracked" instead of poisoning the panel. Swallowed here rather than left to
 * `useLocalStorage`'s own catch so a stale key doesn't log on every mount.
 */
const parseTrackedIds = (raw: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
};

// Module-level so the options object keeps a stable identity across renders.
const TRACKED_IDS_STORAGE_OPTIONS = { deserializer: parseTrackedIds };

/**
 * The app-supplied fetch. Each app owns its own `$api` and endpoint; this
 * package owns the tracking and polling policy. Implementations must call the
 * app's query hook unconditionally, so the hook order stays stable.
 */
export type BulkActionsQuery = (args: {
  enabled: boolean;
  refetchInterval: (actions: BulkAction[] | undefined) => number | false;
}) => { data: BulkAction[] | undefined };

export interface BulkActionsContextValue {
  /** Tracked actions, most recent first, hydrated with live progress. */
  actions: BulkAction[];
  /** Start tracking a freshly-triggered bulk action and open the panel. */
  trackBulkAction: (workflowId: string) => void;
  /** Stop tracking an action (remove its card from the panel). */
  dismiss: (workflowId: string) => void;
  /** Stop tracking every finished action. */
  dismissAllFinished: () => void;
  /** The host's per-type handlers, for the panel to look its rows' up in. */
  handlersByType: BulkActionHandlersByType;
}

export const BulkActionsContext = createContext<BulkActionsContextValue | null>(
  null,
);

interface BulkActionsProviderProps {
  children: ReactNode;
  useActionsQuery: BulkActionsQuery;
  /**
   * The path `useActionsQuery` polls (e.g. `"/v1/bulk-actions"`). Identifies the
   * poll's own query key, which this package needs from both directions: to
   * fetch it when a new action is tracked, and to exclude it from the
   * refresh-on-finish invalidation below. The app owns the endpoint, this
   * package owns the refresh policy.
   */
  actionsQueryPath: string;
  /** Override when two apps must not share one browser's tracked-id list. */
  storageKey?: string;
  /**
   * What each bulk-action type does when it settles or is downloaded — Portus
   * starts its report ZIP downloading from `onSettled`. `onSettled` fires with
   * the same once-per-session eligibility the refresh-on-finish invalidation
   * below consumes; see `BulkActionTypeHandlers`.
   */
  handlersByType?: BulkActionHandlersByType;
}

export const BulkActionsProvider = ({
  children,
  useActionsQuery,
  actionsQueryPath,
  storageKey = DEFAULT_STORAGE_KEY,
  handlersByType = NO_HANDLERS,
}: BulkActionsProviderProps) => {
  const queryClient = useQueryClient();

  // Ids this session triggered, awaiting their one refresh. Ids rehydrated from
  // localStorage are absent on purpose — a reload already fetched fresh data.
  const awaitingRefresh = useRef(new Set<string>());

  // Persisted so a reload resumes showing in-flight actions: `useLocalStorage`
  // writes through on every set, so there is no effect to keep in sync.
  const [trackedIds, setTrackedIds] = useLocalStorage<string[]>(
    storageKey,
    NO_TRACKED_IDS,
    TRACKED_IDS_STORAGE_OPTIONS,
  );

  const refetchInterval = useCallback(
    (fetched: BulkAction[] | undefined) => {
      const tracked = (fetched ?? []).filter((a) => trackedIds.includes(a.id));
      if (tracked.some(isBulkActionRunning)) return POLLING_INTERVAL_MS;
      // Nothing is running, but a finished action is still offering a link the
      // server re-mints on each poll. Keep it alive rather than leaving the
      // user a URL that expires under them — see LINK_REFRESH_INTERVAL_MS.
      const hasLiveLink = tracked.some(
        (action) => isBulkActionFinished(action) && Boolean(action.downloadUrl),
      );
      return hasLiveLink ? LINK_REFRESH_INTERVAL_MS : false;
    },
    [trackedIds],
  );

  const { data } = useActionsQuery({
    enabled: trackedIds.length > 0,
    refetchInterval,
  });

  const trackBulkAction = useCallback(
    (workflowId: string) => {
      awaitingRefresh.current.add(workflowId);
      setTrackedIds((prev) =>
        prev.includes(workflowId) ? prev : [workflowId, ...prev],
      );
      // Growing `trackedIds` is not a refetch trigger: React Query fetches on
      // mount, when `enabled` flips true, or on a timer. An empty tray works
      // only by accident — the first id flips `enabled` false→true. A tray still
      // holding an un-dismissed card is already enabled, so the new action would
      // stay invisible until the next `refetchInterval` tick (five minutes while
      // a finished card keeps its link alive) or a remount. The workflow exists
      // server-side before this resolves, so ask for it now.
      void queryClient.invalidateQueries({
        queryKey: ["get", actionsQueryPath],
      });
    },
    [setTrackedIds, queryClient, actionsQueryPath],
  );

  const dismiss = useCallback(
    (workflowId: string) => {
      awaitingRefresh.current.delete(workflowId);
      setTrackedIds((prev) => prev.filter((id) => id !== workflowId));
    },
    [setTrackedIds],
  );

  // Only the tracked actions the server still knows about, newest first.
  const actions = useMemo<BulkAction[]>(() => {
    const byId = new Map((data ?? []).map((action) => [action.id, action]));
    return trackedIds
      .map((id) => byId.get(id))
      .filter((action): action is BulkAction => Boolean(action));
  }, [data, trackedIds]);

  // A bulk action this session triggered reached a terminal status: rows under
  // the panel changed (failed/cancelled included — partial progress persists),
  // so refetch every mounted "get" query. Excluding the poll's own key keeps
  // the invalidation from re-triggering this effect; consuming eligibility
  // before invalidating makes the refresh exactly-once per action — which is
  // also what `onSettled` promises its host.
  useEffect(() => {
    const settled = actions.filter(
      (a) => isBulkActionFinished(a) && awaitingRefresh.current.has(a.id),
    );
    if (settled.length === 0) return;
    settled.forEach((a) => awaitingRefresh.current.delete(a.id));

    settled.forEach((action) =>
      handlersByType[action.type]?.onSettled?.(action),
    );

    void queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === "get" && queryKey[1] !== actionsQueryPath,
    });
  }, [actions, actionsQueryPath, queryClient, handlersByType]);

  const dismissAllFinished = useCallback(() => {
    const runningIds = new Set(
      actions.filter(isBulkActionRunning).map((a) => a.id),
    );
    setTrackedIds((prev) => prev.filter((id) => runningIds.has(id)));
  }, [actions, setTrackedIds]);

  const value = useMemo<BulkActionsContextValue>(
    () => ({
      actions,
      trackBulkAction,
      dismiss,
      dismissAllFinished,
      handlersByType,
    }),
    [actions, trackBulkAction, dismiss, dismissAllFinished, handlersByType],
  );

  return (
    <BulkActionsContext.Provider value={value}>
      {children}
    </BulkActionsContext.Provider>
  );
};

export const useBulkActions = (): BulkActionsContextValue => {
  const context = useContext(BulkActionsContext);
  if (!context) {
    throw new Error("useBulkActions must be used within a BulkActionsProvider");
  }
  return context;
};
